import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import VaultoLogo from './VaultoLogo';
import LanguagePicker from './LanguagePicker';
import {
  ChartBarIcon,
  DocumentCheckIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  LinkIcon,
  ArrowUpTrayIcon,
  // UsersIcon, // Hidden for now - Audience link disabled
} from '@heroicons/react/24/outline';

const Navbar = ({ variant = 'default' }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Different styles based on variant
  const getNavbarStyles = () => {
    if (variant === 'transparent' || location.pathname === '/') {
      return 'absolute top-0 left-0 right-0 z-[60] bg-transparent';
    }
    return 'sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-200/50 shadow-sm z-[60]';
  };

  const getLinkStyles = () => {
    if (variant === 'transparent' || location.pathname === '/') {
      return 'text-white/80 hover:text-white';
    }
    return 'text-gray-600 hover:text-gray-900';
  };

  // Check if user has premium tier or above
  const isPremiumOrAbove = user?.subscription_tier === 'premium' || 
                           user?.subscription_tier === 'pro' || 
                           user?.subscription_tier === 'agency';

  // Navigation for all authenticated users (including affiliates)
  const navigation = [
    { name: t('navbar.dashboard'), href: '/dashboard', icon: ChartBarIcon },
    { name: t('navbar.backups'), href: '/backups', icon: DocumentCheckIcon },
    // Audience link hidden for now - page still accessible via direct URL
    // { name: t('navbar.audience', 'Audience'), href: '/audience', icon: UsersIcon },
    ...(isPremiumOrAbove ? [{ name: t('navbar.restore', 'Restore'), href: '/restore', icon: ArrowUpTrayIcon }] : []),
    { name: t('navbar.settings'), href: '/settings', icon: Cog6ToothIcon },
    { name: t('navbar.pricing'), href: '/pricing', icon: CurrencyDollarIcon },
  ];

  // Add affiliate dashboard link if user is an affiliate
  if (user?.is_affiliate) {
    navigation.push({ name: 'Affiliate Dashboard', href: '/affiliate-dashboard', icon: LinkIcon });
  }

  // Add admin link if user is admin
  if (user?.is_admin) {
    navigation.push({ name: t('navbar.admin'), href: '/admin', icon: ShieldCheckIcon });
  }

  const isActive = (href) => location.pathname === href;

  return (
    <nav className={getNavbarStyles()}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group flex-shrink-0 mr-8">
            <VaultoLogo 
              className="h-10 w-auto sm:h-12" 
              showText={true}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-6">
            {!user && (
              <>
                {/* Features link for home page */}
                {location.pathname === '/' && (
                  <a
                    href="#features"
                    className={`${getLinkStyles()} px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/10`}
                  >
                    {t('navbar.features')}
                  </a>
                )}
                
                {/* Pricing link */}
                <Link
                  to="/pricing"
                  className={`${getLinkStyles()} px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    location.pathname === '/pricing' 
                      ? 'bg-white/10 backdrop-blur-sm' 
                      : 'hover:bg-white/10'
                  }`}
                >
                  {t('navbar.pricing')}
                </Link>
                
                {/* Auth Links */}
                <div className="flex items-center space-x-3">
                  <LanguagePicker variant={variant === 'transparent' || location.pathname === '/' ? 'transparent' : 'default'} />
                  <Link
                    to="/login"
                    className={`${getLinkStyles()} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      location.pathname === '/login' 
                        ? 'bg-white/10 backdrop-blur-sm' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {t('navbar.signIn')}
                  </Link>
                  <Link
                    to="/register"
                    className={`group relative overflow-hidden px-6 py-2 text-sm font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 ${
                      location.pathname === '/register'
                        ? 'bg-white text-purple-600 shadow-lg'
                        : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg hover:shadow-xl animate-pulse hover:animate-none'
                    }`}
                  >
                    <span className="relative z-10 flex items-center">
                      {t('navbar.getStarted')}
                      <span className="ml-1.5 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">Free</span>
                    </span>
                    {/* Animated background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                  </Link>
                </div>
              </>
            )}

            {/* Authenticated user navigation */}
            {user && (
              <>
                {/* Navigation Links for authenticated users */}
                <div className="flex space-x-1">
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActive(item.href)
                            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                            : `${getLinkStyles()} hover:bg-white/10`
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>

                {/* User info and logout */}
                <div className="flex items-center space-x-4">
                  <LanguagePicker variant={variant === 'transparent' || location.pathname === '/' ? 'transparent' : 'default'} />
                  <div className="flex items-center">
                    <span className={`text-sm font-medium ${getLinkStyles()} hidden lg:block`}>
                      {user?.full_name || user?.email}
                    </span>
                    <div className="ml-3 h-8 w-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white text-sm font-medium">
                        {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={logout}
                    className={`hidden lg:inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg transition-all duration-200 ${
                      variant === 'transparent' || location.pathname === '/'
                        ? 'text-white bg-white/10 hover:bg-white/20 border-white/20'
                        : 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300 shadow-sm'
                    }`}
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                    {t('navbar.logout')}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Mobile Section */}
          <div className="flex md:hidden items-center">
            {/* Show user avatar only when logged in */}
            {user && (
              <div className="h-8 w-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg mr-3">
                <span className="text-white text-sm font-medium">
                  {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            
            {/* Hamburger Menu Button for all mobile users */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`inline-flex items-center justify-center p-2 rounded-lg transition-all duration-200 ${
                variant === 'transparent' || location.pathname === '/'
                  ? 'text-white bg-white/10 hover:bg-white/20'
                  : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
              }`}
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-5 w-5" />
              ) : (
                <Bars3Icon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className={`border-t ${
            variant === 'transparent' || location.pathname === '/'
              ? 'border-white/20 bg-black/20 backdrop-blur-md'
              : 'border-gray-200 bg-white/95 backdrop-blur-md'
          }`}>
            <div className="px-4 py-3 space-y-1">
              {user ? (
                <>
                  {/* User info */}
                  <div className={`px-3 py-2 ${getLinkStyles()}`}>
                    <div className="text-sm font-medium">
                      {user?.full_name || user?.email}
                    </div>
                  </div>
                  
                  {/* Navigation Links */}
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActive(item.href)
                            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                            : `${getLinkStyles()} hover:bg-white/10`
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-3" />
                        {item.name}
                      </Link>
                    );
                  })}

                  {/* Logout Button */}
                  <button
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      variant === 'transparent' || location.pathname === '/'
                        ? 'text-white bg-white/10 hover:bg-white/20'
                        : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                    {t('navbar.logout')}
                  </button>
                </>
              ) : (
                <>
                  {/* Features link for home page */}
                  {location.pathname === '/' && (
                    <a
                      href="#features"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${getLinkStyles()} hover:bg-white/10`}
                    >
                      {t('navbar.features')}
                    </a>
                  )}
                  
                  {/* Pricing link for mobile */}
                  <Link
                    to="/pricing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      location.pathname === '/pricing'
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                        : `${getLinkStyles()} hover:bg-white/10`
                    }`}
                  >
                    <CurrencyDollarIcon className="h-4 w-4 mr-3" />
                    {t('navbar.pricing')}
                  </Link>
                  
                  {/* Language Picker for Mobile */}
                  <div className="px-3 py-2">
                    <LanguagePicker variant={variant === 'transparent' || location.pathname === '/' ? 'transparent' : 'default'} />
                  </div>
                  
                  {/* Mobile Auth Links */}
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      location.pathname === '/login'
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                        : `${getLinkStyles()} hover:bg-white/10`
                    }`}
                  >
                    {t('navbar.signIn')}
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center justify-center px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${
                      location.pathname === '/register'
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                        : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg hover:from-pink-600 hover:to-purple-700'
                    }`}
                  >
                    🚀 {t('navbar.getStarted')} - It's Free!
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
